/**
 * HTTP contract between the Cloudflare Worker (apps/api) and the web app (apps/web).
 *
 * FROZEN CONTRACT for parallel work. The Worker and the web app are served from
 * the same origin, so no CORS is needed: the web app sends a Clerk session token
 * (bearer header or Clerk's first-party __session cookie), or locally the dev
 * sign-in's HttpOnly cookie.
 * Every JSON error response uses ApiError.
 *
 * Auth additions for Clerk (ADR-0021) live in auth.ts.
 *
 * Milestone M3 types live in one module file per feature, each owned by one
 * builder and re-exported below: protocol.ts (F12 freeze), search.ts (F13),
 * dedup.ts (F14), screening.ts (F15), runs.ts (F18), journey.ts (F23), billing.ts (F24), extraction.ts (F16), exports.ts (F20), living.ts (F18 schedules and candidates). Each file exports its own
 * route table, spread into ROUTES, so a builder adds routes without editing
 * this file.
 */
import type {
  Certainty,
  EligibilityRule,
  EvidencePage,
  OutcomeDefinition,
  Pico,
  ReviewType,
  Snapshot,
  StudyDesign,
} from '@bioterafi/evidence-model';
import { ALERT_ROUTES } from './alerts';
import { PUBLIC_API_PATHS } from './publicApi';
import { AUTH_ROUTES } from './auth';
import { BILLING_ROUTES } from './billing';
import { DEDUP_ROUTES } from './dedup';
import { EMAIL_ROUTES } from './email';
import { EXPORT_ROUTES } from './exports';
import { ADMIN_ROUTES } from './admin';
import { EXTRACTION_ROUTES } from './extraction';
import { GRADE_ROUTES } from './grade';
import { JOURNEY_ROUTES, type ReviewJourneyResponse } from './journey';
import { LIVING_ROUTES } from './living';
import { PROTOCOL_ROUTES, type PicoDraftProtocolFields, type QuestionProtocolFields, type SaveQuestionProtocolFields } from './protocol';
import { RUN_ROUTES } from './runs';
import { SCREENING_ROUTES } from './screening';
import { ROB_ROUTES } from './rob';
import { SEARCH_ROUTES } from './search';

export * from './paths';
export * from './auth';
export * from './protocol';
export * from './search';
export * from './dedup';
export * from './screening';
export * from './runs';
export * from './journey';
export * from './billing';
export * from './extraction';
export * from './grade';
export * from './exports';
export * from './admin';
export * from './rob';
export * from './living';
export * from './email';
export * from './alerts';
export * from './publicApi';

export const ROUTES = {
  // Retired since ADR-0021 (Clerk): both redirect to /login (AUTH-160). Kept so old links keep a known shape.
  authStart: (provider: AuthProvider) => `/auth/${provider}/start`,
  authCallback: (provider: AuthProvider) => `/auth/${provider}/callback`,
  /** POST. Clears the dev session and audits the sign-out (AUTH-082, AUTH-165). */
  logout: '/auth/logout',

  /** GET MeResponse; DELETE DeleteAccountRequest -> 204 (E57; AUTH-164). */
  me: '/api/v1/me',
  dashboard: '/api/v1/dashboard',
  evidencePages: '/api/v1/evidence-pages',
  evidencePage: (slug: string) => `/api/v1/evidence-pages/${encodeURIComponent(slug)}`,
  snapshots: (slug: string) => `/api/v1/evidence-pages/${encodeURIComponent(slug)}/snapshots`,
  picoDraft: '/api/v1/questions/pico-draft',
  questions: '/api/v1/questions',
  /** GET, PUT; DELETE of a draft question (E20; QB-067). */
  question: (id: string) => `/api/v1/questions/${encodeURIComponent(id)}`,

  ...AUTH_ROUTES,
  ...PROTOCOL_ROUTES,
  ...SEARCH_ROUTES,
  ...DEDUP_ROUTES,
  ...SCREENING_ROUTES,
  ...RUN_ROUTES,
  ...JOURNEY_ROUTES,
  ...BILLING_ROUTES,
  ...EXTRACTION_ROUTES,
  ...GRADE_ROUTES,
  ...EXPORT_ROUTES,
  ...ADMIN_ROUTES,
  ...ROB_ROUTES,
  ...LIVING_ROUTES,
  ...EMAIL_ROUTES,
  ...ALERT_ROUTES,
  ...PUBLIC_API_PATHS,
} as const;

/** Query parameter on authStart: same-origin path to return to after sign-in. */
export const RETURN_TO_PARAM = 'returnTo';

export interface ApiError {
  error: {
    /**
     * Stable machine code (specs/architecture/api.md section 4): 'invalid_request', 'unauthenticated',
     * 'forbidden', 'not_found', 'conflict', 'question_frozen', 'protocol_frozen', 'payload_too_large',
     * 'unsupported_media_type', 'ai_refused', 'rate_limited', 'internal', 'ai_invalid_output', 'ai_error',
     * 'not_implemented' (501, an M3 endpoint whose builder has not delivered it yet), 'ai_unavailable',
     * 'auth_unavailable' (503, the sign-in service could not return a new user's profile; AUTH-159).
     */
    code: string;
    message: string;
    details?: unknown;
  };
}

// ---------- Auth ----------

export type AuthProvider = 'google' | 'orcid';

export interface SessionUser {
  id: string;
  name: string;
  email: string | null;
  /** ORCID iD, e.g. "0000-0002-1825-0097", when an ORCID account is linked. */
  orcid: string | null;
  avatarUrl: string | null;
  providers: AuthProvider[];
}

/** GET me. 401 with ApiError code 'unauthenticated' when signed out. */
export interface MeResponse {
  user: SessionUser;
}

// ---------- Evidence pages ----------

export interface EvidencePageListing {
  slug: string;
  title: string;
  studyCount: number;
  dataOriginKind: EvidencePage['dataOrigin']['kind'];
  visibility: EvidencePage['visibility'];
  updatedAt: string;
  /** True when the signed-in user's workspace owns the page. False when signed out. */
  ownedByMe: boolean;
}

/** GET evidencePages. Signed out: shared pages only. Signed in: shared pages plus the user's own. */
export interface EvidencePageListResponse {
  pages: EvidencePageListing[];
}

/** GET evidencePage(slug): the EvidencePage JSON itself. Private pages need an owning session (else 404). */
export type EvidencePageResponse = EvidencePage;

/** POST evidencePages: import or replace a page by slug. Signed in only. */
export interface ImportEvidencePageResponse {
  slug: string;
  snapshotId: string | null;
}

export interface SnapshotListResponse {
  snapshots: Snapshot[];
}

// ---------- Dashboard ----------

export type PageLifecycle = 'draft' | 'protocol_frozen' | 'living';

export type AlertTrigger = 'study_added' | 'crossed_null' | 'certainty_changed' | 'study_retracted';

export interface DashboardPage extends EvidencePageListing {
  lifecycle: PageLifecycle;
  lastRunAt: string | null;
  nextRunAt: string | null;
  /** Machine-extracted values awaiting human confirmation. */
  pendingConfirmations: number;
  latestChange: { at: string; summary: string } | null;
  certainty: Certainty | null;
  /** Where the review stands, for pages built from a question; null for imported pages (JRNY-012). */
  journey: ReviewJourneyResponse | null;
}

export interface DashboardAlert {
  id: string;
  pageSlug: string;
  pageTitle: string;
  trigger: AlertTrigger;
  message: string;
  createdAt: string;
  read: boolean;
}

export interface QuestionDraftListing {
  id: string;
  /** Short title derived from the PICO, or the free text truncated. */
  title: string;
  status: QuestionStatus;
  updatedAt: string;
}

/** GET dashboard. Signed in only. */
export interface DashboardResponse {
  user: SessionUser;
  myPages: DashboardPage[];
  /** Shared evidence pages the user does not own (includes the seed library). */
  library: EvidencePageListing[];
  questions: QuestionDraftListing[];
  alerts: DashboardAlert[];
}

// ---------- Question builder (spec 5.1) ----------

export type QuestionStatus = 'draft' | 'protocol_frozen';

/**
 * An outcome before freeze. `type` and `unit` were added on 14 Oct 2026 (QB-133): a stored outcome
 * without them reads as binary with no unit, and the Worker accepts a save that omits them.
 */
export type DraftOutcome = Pick<OutcomeDefinition, 'name' | 'type' | 'role' | 'definition' | 'timepoint' | 'undesirable'> & {
  /** Unit of a continuous or rate outcome ("mmHg", "per 1000 person-years"); null otherwise. */
  unit: string | null;
};

/** The framing of a question (QB-130, QB-131). Added on 14 Oct 2026; a stored question without it is an intervention review of RCTs. */
export interface QuestionFraming {
  reviewType: ReviewType;
  /** One to nine designs in STUDY_DESIGNS order. */
  studyDesigns: StudyDesign[];
}

/** POST picoDraft. Signed in only. Free-text clinical question to a structured draft for the user to edit. */
export interface PicoDraftRequest {
  question: string;
}

export interface PicoDraft extends QuestionFraming {
  pico: Pico;
  eligibility: EligibilityRule[];
  /** Exactly one primary, at most five secondary (spec 5.1). */
  outcomes: DraftOutcome[];
  /** Ambiguities the user should resolve before freezing the protocol. */
  clarifyingQuestions: string[];
}

export interface PicoDraftResponse extends PicoDraftProtocolFields {
  draft: PicoDraft;
  /** Recorded per decision (spec: model versions are recorded). */
  model: { provider: 'anthropic'; id: string };
  generatedAt: string;
}

export interface Question extends QuestionProtocolFields, QuestionFraming {
  id: string;
  freeText: string;
  pico: Pico;
  eligibility: EligibilityRule[];
  outcomes: DraftOutcome[];
  status: QuestionStatus;
  /** Model that produced the original draft, if any: derived on the server from SaveQuestionRequest.draftModelCallId (QB-062). */
  draftedBy: { provider: 'anthropic'; id: string } | null;
  createdAt: string;
  updatedAt: string;
}

/** POST questions (create) and PUT question(id) (update while status is 'draft'). */
export interface SaveQuestionRequest extends SaveQuestionProtocolFields, QuestionFraming {
  freeText: string;
  pico: Pico;
  eligibility: EligibilityRule[];
  outcomes: DraftOutcome[];
}

export type QuestionResponse = Question;
